import type { Account, Category, Dashboard } from '../api/contract';

export type StructureFormDirty = {
  name: boolean;
  amount: boolean;
  goal: boolean;
  includeInTotal: boolean;
  color: boolean;
  icon: boolean;
};

export type StructureFormValues = {
  name: string;
  includeInTotal: boolean;
  color: string;
  icon: string;
};

export function structureFormAmountMinor(
  category: Category | undefined,
  account: Account | undefined,
  dashboard: Dashboard | undefined,
): number | undefined {
  if (dashboard === undefined) return undefined;
  if (account !== undefined) {
    return dashboard.budgetSummaries.find(
      item => item.budgetId === account.id,
    )?.balance;
  }
  if (category?.type === 'INCOME') {
    return dashboard.amountByCategory[category.id];
  }
  if (category?.type === 'EXPENSE') {
    return dashboard.categorySummaries.find(
      item => item.categoryId === category.id,
    )?.limitMinor;
  }
  return undefined;
}

export function buildCategoryUpdateBody(
  kind: 'income' | 'expense',
  values: StructureFormValues,
  dirty: StructureFormDirty,
  amountMinor: number | undefined,
): Record<string, string | number | boolean> {
  return {
    ...(dirty.name ? { name: values.name.trim() } : {}),
    ...(dirty.color ? { color: values.color } : {}),
    ...(dirty.icon ? { icon: values.icon } : {}),
    ...(kind === 'income' && dirty.includeInTotal
      ? { includeInTotal: values.includeInTotal }
      : {}),
    ...(dirty.amount && amountMinor !== undefined
      ? kind === 'income'
        ? { amountMinor }
        : { limitMinor: amountMinor }
      : {}),
  };
}

export type BudgetUpdatePlan =
  | { target: 'none' }
  | {
      target: 'account';
      body: Record<string, string>;
    }
  | {
      target: 'profile';
      body: {
        name: string;
        limitMinor: number;
        goalMinor: number | null;
        includeInTotal: boolean;
        color: string;
        icon: string;
        balanceAdjustmentMinor?: number;
      };
    };

export function buildBudgetUpdatePlan(
  account: Account,
  values: StructureFormValues,
  dirty: StructureFormDirty,
  amountMinor: number | undefined,
  goalMinor: number | null,
): BudgetUpdatePlan {
  const metadataDirty = dirty.name || dirty.color || dirty.icon;
  const profileDirty = dirty.amount || dirty.goal || dirty.includeInTotal;
  if (!metadataDirty && !profileDirty) return { target: 'none' };
  if (!profileDirty) {
    return {
      target: 'account',
      body: {
        ...(dirty.name ? { name: values.name.trim() } : {}),
        ...(dirty.color ? { color: values.color } : {}),
        ...(dirty.icon ? { icon: values.icon } : {}),
      },
    };
  }
  return {
    target: 'profile',
    body: {
      name: values.name.trim(),
      limitMinor:
        account.profile.type === 'GOAL'
          ? account.profile.limitMinor
          : dirty.amount && amountMinor !== undefined
            ? amountMinor
            : account.profile.limitMinor,
      goalMinor,
      includeInTotal: values.includeInTotal,
      color: values.color,
      icon: values.icon,
      ...(!dirty.amount || account.profile.type === 'GOAL'
        ? {
            balanceAdjustmentMinor:
              account.profile.balanceAdjustmentMinor,
          }
        : {}),
    },
  };
}
